import { setWithinPercentageRange } from "@@/utilities/set-within-range";
import type { Color } from "./color";
import { colorValueFactory } from "./color-value-factory";
import { colorVariations } from "./color-variation";
import type { HexColor } from "./hex-color/hex-color";
import type { HSL } from "./hsla-color/hsl-color";

const colorCache = new Map<string, Color>();
function colorFactory(hexColor: HexColor): Color;
function colorFactory(params: HSL & { alpha?: number }): Color;
function colorFactory(params: HexColor | (HSL & { alpha?: number })): Color {
    const input =
        typeof params !== "string" && "alpha" in params
            ? { ...params, alpha: setWithinPercentageRange(params.alpha ?? 1) }
            : params;

    // Check cache early
    const colorValue = colorValueFactory(input);
    const fromCache = colorCache.get(colorValue.asString);
    if (fromCache) return fromCache;

    // Nothing in cache, create a new color
    const color: Partial<Color> = {
        _type: "COLOR",
        value: colorValue,
        asString: colorValue.asString,
        withAlpha: (alpha: number) =>
            colorFactory({
                hue: colorValue.hue,
                saturation: colorValue.saturation,
                light: colorValue.light,
                alpha: setWithinPercentageRange(alpha ?? 1),
            }),
    };

    // Create color variations
    for (const variation of colorVariations) {
        const lightVariation = parseInt(variation.replace("light", ""));
        if (isNaN(lightVariation)) continue;
        color[variation] = colorValueFactory({
            hue: colorValue.hue,
            saturation: colorValue.saturation,
            light: setWithinPercentageRange(lightVariation / 100),
            alpha: colorValue.alpha,
        });
    }

    // Cache the result and return
    const result = color as Color;
    colorCache.set(colorValue.asString, result);

    return result;
}

export { colorFactory };
